declare global {
  interface Window {
    __darvin_lastMouseEvent?: MouseEvent;
  }
}

import { useEffect, useRef } from "react";
import { LuPanelLeftOpen } from "react-icons/lu";
import { useAuthUser } from "../../core_stores/useAuthStore";
import { colors } from "../../design/tokens";
import {
  SideMenuBehavior,
  useCanReopenSideMenu,
  useSideMenuActions,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";

export default function SideMenuPanel(): JSX.Element | null {
  const { open: openSideMenu, setCanReopenMenu } = useSideMenuActions();
  const sideMenuBehavior = useSideMenuBehavior();
  const canReopenMenu = useCanReopenSideMenu();
  const panelRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function handleBlockReopen(): void {
      if (!panelRef.current) return;

      const rect = panelRef.current.getBoundingClientRect();
      const { clientX, clientY } = window.__darvin_lastMouseEvent || {};

      const isCursorOverPanel =
        typeof clientX === "number" &&
        typeof clientY === "number" &&
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;

      if (isCursorOverPanel || sideMenuBehavior === SideMenuBehavior.PINNED) {
        setCanReopenMenu(false);
      }
    }

    window.addEventListener("darvin-block-reopen-menu", handleBlockReopen);
    return () => window.removeEventListener("darvin-block-reopen-menu", handleBlockReopen);
  }, [sideMenuBehavior, setCanReopenMenu]);

  useEffect(() => {
    function handleMouseMove(e: MouseEvent): void {
      window.__darvin_lastMouseEvent = e;
    }

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  function handleMouseEnter(): void {
    if (!canReopenMenu) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      openSideMenu();
    }, 100);
  }

  function handleMouseLeave(): void {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setCanReopenMenu(true);
  }

  if (sideMenuBehavior === SideMenuBehavior.PINNED) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`h-full w-[56px] flex flex-col items-center justify-end fixed left-0 overflow-hidden
        ${colors.surface.primary} border-r ${colors.border.primary}
        transform transition-transform duration-300 ease-in-out z-40`}
    >
      <div className="w-full flex flex-col items-center pb-6 gap-4">
        <Avatar />
        <button
          onClick={openSideMenu}
          className="w-10 h-10 flex items-center justify-center rounded-lg
            text-dark-text-muted hover:text-dark-text hover:bg-dark-hover
            transition-colors duration-200"
        >
          <LuPanelLeftOpen size={22} />
        </button>
      </div>
    </div>
  );
}

function Avatar(): JSX.Element {
  const user = useAuthUser();

  return (
    <div className="w-[28px] h-[28px] rounded-full overflow-hidden cursor-pointer hover:opacity-80 transition-opacity">
      <img
        src={user?.photoURL || ""}
        alt="User avatar"
        className="w-full h-full object-cover"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}
